import { useMutation } from '@tanstack/react-query';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

import type {
  BillingIntent,
  TransferCreditMode as TransferCreditModeType,
} from './get-remaining-credit';
import {
  TransferCreditMode,
  TransferErrorCode,
} from './get-remaining-credit';
import type { EnrollmentPaymentProvider } from '~entities/payment/api/get-enrollment-payment-methods';

export { TransferCreditMode, TransferErrorCode };

/** V1 transfer response when parent has no saved payment method. */
export const TRANSFER_PAYMENT_METHOD_NOT_FOUND_MESSAGE =
  'Payment method not found';

export interface TransferCreditBreakdown {
  availableCredit: number;
  creditAppliedToNewClass: number;
  creditAddedToWallet: number;
  amountToBeCharged: number;
  currentTuitionFee: number;
  newTuitionFee: number;
  effectiveBillingIntent: BillingIntent;
  prorationDisabledAtStudioLevel?: boolean;
  creditMode?: TransferCreditModeType;
  previewSummary?: {
    description: string;
    oldClassImpact: string;
    newClassImpact: string;
  };
}

export interface DestinationEnrollmentResult {
  outcome?: string;
  results?: Array<{
    studentId: string;
    entityId: string;
    status: string;
    message?: string;
  }>;
  message?: string;
  amountDueTotal?: number;
}

export interface TransferClassParams {
  studioId: string;
  currentClassId: string;
  newClassId: string;
  studentId: string;
  billingIntent?: BillingIntent;
  creditMode?: TransferCreditModeType;
  customCreditAmount?: number;
  availableCredit?: number;
  creditAppliedToNewClass?: number;
  creditAddedToWallet?: number;
  skipPaymentMethod?: boolean;
  paymentMethodId?: string;
  paymentProvider?: EnrollmentPaymentProvider;
  /** When true, use V2 transfer execute. When false/undefined, use V1. */
  isV2?: boolean;
  /** V2 opt-in destination fees (independent of tuition / proration). */
  chargeMembershipFee?: boolean;
  chargeCostumeFee?: boolean;
  /** V1 only — amount of credit to apply. */
  newCreditAmount?: number;
  /** V1 only — when true, do not apply previous class credit. */
  noCreditRequired?: boolean;
}

export interface TransferClassResponse {
  transferred?: boolean;
  sourceCancelled?: boolean;
  message?: string;
  didRollbackDestination?: boolean;
  transferCredit?: TransferCreditBreakdown;
  destinationEnrollment?: DestinationEnrollmentResult;
  /** @deprecated V1 compat — prefer `transferred` */
  transferCompleted?: boolean;
  /** @deprecated V1 compat */
  sourceEnrollmentPreserved?: boolean;
  status?: number;
  clientSecret?: string;
  stripePublicApiKey?: string;
  stripeAccount?: string;
}

interface V2TransferExecuteResponse {
  transferred: boolean;
  sourceCancelled: boolean;
  message: string;
  didRollbackDestination?: boolean;
  transferCredit?: TransferCreditBreakdown;
  destinationEnrollment?: DestinationEnrollmentResult;
}

const transferClassV1API = async (body: TransferClassParams) => {
  const noCreditRequired =
    body.noCreditRequired ?? body.creditMode === TransferCreditMode.NONE;
  const newCreditAmount =
    body.newCreditAmount ??
    (body.creditMode === TransferCreditMode.NONE
      ? 0
      : (body.availableCredit ?? 0));

  const resp = await baseAPI.post<TransferClassResponse>(
    `payment-processor/subscription/transfer`,
    {
      currentClassId: body.currentClassId,
      newClassId: body.newClassId,
      studentId: body.studentId,
      noCreditRequired,
      newCreditAmount,
      availableCredit: body.availableCredit,
      creditAppliedToNewClass: body.creditAppliedToNewClass,
      creditAddedToWallet: body.creditAddedToWallet,
      billingIntent: body.billingIntent,
      skipPaymentMethod: body.skipPaymentMethod,
    },
  );
  return resp.data;
};

const transferClassV2API = async (body: TransferClassParams) => {
  const resp = await baseAPI.post<V2TransferExecuteResponse>(
    `/v2/class-transfers`,
    {
      studioId: body.studioId,
      studentId: body.studentId,
      currentClassId: body.currentClassId,
      newClassId: body.newClassId,
      billingIntent: body.billingIntent,
      creditMode: body.creditMode,
      ...(body.customCreditAmount !== undefined
        ? { customCreditAmount: body.customCreditAmount }
        : {}),
      availableCredit: body.availableCredit,
      creditAppliedToNewClass: body.creditAppliedToNewClass,
      creditAddedToWallet: body.creditAddedToWallet,
      skipPaymentMethod: body.skipPaymentMethod,
      ...(body.paymentMethodId
        ? { paymentMethodId: body.paymentMethodId }
        : {}),
      ...(body.paymentProvider
        ? { paymentProvider: body.paymentProvider }
        : {}),
      chargeMembershipFee: body.chargeMembershipFee === true,
      chargeCostumeFee: body.chargeCostumeFee === true,
    },
  );

  return {
    ...resp.data,
    transferCompleted: resp.data.transferred,
    message: resp.data.message,
  } satisfies TransferClassResponse;
};

export const transferClassAPI = async (body: TransferClassParams) => {
  if (body.isV2) {
    return transferClassV2API(body);
  }
  return transferClassV1API(body);
};

export const useTransferClassMutation = () => {
  return useMutation({
    mutationFn: async (body: TransferClassParams) => {
      const response = await transferClassAPI(body);
      return response;
    },

    onSuccess: (_, { studentId }) => {
      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === KEYS.CLASSES ||
          query.queryKey[0] === KEYS.STUDENT_CLASS_HISTORY ||
          (query.queryKey[0] === KEYS.STUDENTS &&
            query.queryKey[1] === studentId),
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ACCOUNTS_RECEIVABLE],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ACCOUNTS_RECEIVABLE_DUE_DATE_GROUPING],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ACCOUNTS_RECEIVABLE_STUDENT_GROUPING],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.TOTAL_ACTIVE_CLASSES],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.TOTAL_OUTSTANDING_REVENUE],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.TOTAL_SCHEDULED_UPCOMING_REVENUE],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.TOP_10_ENROLLED_STUDENTS],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.CLASS_PERFORMANCE_LOW_ENROLLMENT],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.CLASS_PERFORMANCE_NEAR_CAPACITY],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.NINETY_PERCENT_FULL_CLASSES],
      });
    },
  });
};
